import React, { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Upload, Plus, Loader2, Boxes, PackageCheck, Truck, Wrench } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { api, getApiErrorMessage } from "../../api/client";
import ManageCategoriesDialog from "../../components/rentals/ManageCategoriesDialog";
import { PageHeaderBand } from "../../components/layout/PageHeaderBand";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import { queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import { fmtCurrency } from "../../lib/display-labels";
import { displayValue } from "../../features/fieldiq-handoff/contracts";
import { useOpenRecord } from "../downhole/recordPage";
import { useUrlBackedText } from "../../hooks/useUrlBackedText";
import {
  type Asset,
  type AssetForm,
  AssetFormFields,
  RENTAL_ASSETS_KEY,
  StatusBadge,
  UNKNOWN,
  assetBody,
  blankForm,
  skinOf,
  useAssetCategoryNames,
  useRentalAssets,
} from "./rental-asset";

/**
 * /rentals/assets — Rental Equipment, full width. A row (or its View button)
 * opens the asset on its own page, /rentals/assets/:assetId
 * (AssetDetailScreen), where its details and the edit form live. It was a
 * slide-out panel over the list until split panes and drawers beside a list
 * were banned (CLAUDE.md). The status chip, category and search live in the
 * URL (`?status=`, `?category=`, `?q=`) so the asset page's back arrow
 * returns to them.
 */

type ChipKey = "all" | "available" | "rented" | "reserved" | "maintenance" | "retired" | "other";
/**
 * Counts are tallies of the loaded list: GET /assets is unpaginated and no
 * endpoint returns them. "Other" is every status the API does not write.
 */
const CHIPS: { key: ChipKey; label: string; match: (a: Asset) => boolean }[] = [
  { key: "all", label: "All", match: () => true },
  { key: "available", label: "Available", match: (a) => a.status === "available" },
  { key: "rented", label: "On Rent", match: (a) => a.status === "rented" },
  { key: "reserved", label: "Reserved", match: (a) => a.status === "reserved" },
  { key: "maintenance", label: "Maintenance", match: (a) => a.status === "maintenance" },
  { key: "retired", label: "Retired", match: (a) => a.status === "retired" },
  { key: "other", label: "Unrecognised status", match: (a) => skinOf(a.status) === UNKNOWN },
];

const isChipKey = (v: string | null): v is ChipKey => !!v && CHIPS.some((c) => c.key === v);

const AssetRegistry = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const openRecord = useOpenRecord();
  const [searchParams, setSearchParams] = useSearchParams();

  const assetsQuery = useRentalAssets();
  const failed = queryLoadFailure(assetsQuery) != null;
  const loading = assetsQuery.isLoading && !failed;
  const assets = assetsQuery.data;

  const [submitting, setSubmitting] = useState(false);

  // --- filters, in the URL so the asset page's back arrow returns to them ---
  const chipParam = searchParams.get("status");
  const chip: ChipKey = isChipKey(chipParam) ? chipParam : "all";
  const search = searchParams.get("q") ?? "";
  const categoryFilter = searchParams.get("category") || "All";
  const setFilter = (key: "status" | "q" | "category", value: string) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value);
    else p.delete(key);
    setSearchParams(p, { replace: true });
  };
  /* The box keeps its own text and writes it through; see useUrlBackedText. */
  const [searchInput, onSearchInput] = useUrlBackedText(search, (q) => setFilter("q", q));

  // --- add-asset dialog ---
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<AssetForm>(blankForm());
  const [manageCategoriesOpen, setManageCategoriesOpen] = useState(false);

  const categoryNames = useAssetCategoryNames(assets);

  /* ---------------------------------------------------------------- */
  /*  Filter logic                                                     */
  /* ---------------------------------------------------------------- */

  const counts = useMemo(
    () => (assets ? (Object.fromEntries(CHIPS.map((c) => [c.key, assets.filter(c.match).length])) as Record<ChipKey, number>) : null),
    [assets],
  );
  const tally = (k: ChipKey) => (failed || !counts ? "—" : displayValue(counts[k]));

  const displayFiltered = useMemo(() => {
    const q = search.toLowerCase();
    const match = CHIPS.find((c) => c.key === chip)!.match;
    return (assets ?? []).filter((a) => {
      if (!match(a)) return false;
      if (categoryFilter !== "All" && a.category !== categoryFilter) return false;
      if (q && !a.name.toLowerCase().includes(q) && !(a.tag ?? "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [assets, chip, categoryFilter, search]);

  /* ---------------------------------------------------------------- */
  /*  Add-asset handlers                                               */
  /* ---------------------------------------------------------------- */

  const handleAddSubmit = async () => {
    if (!addForm.tag.trim() || !addForm.name.trim() || !addForm.category) {
      toast.error("Please fill in Asset Number, Name, and Category");
      return;
    }
    const built = assetBody(addForm, null);
    if ("error" in built) {
      toast.error(built.error);
      return;
    }
    setSubmitting(true);
    try {
      await api.rentals.assets.create(built.body);
      toast.success("Added to rental equipment");
      setAddForm(blankForm());
      setAddOpen(false);
      await queryClient.invalidateQueries({ queryKey: RENTAL_ASSETS_KEY });
    } catch (err) {
      // "added locally" was a contradiction: the toast said the write failed
      // and the row appeared in the list anyway, with a `local-` id no
      // other screen could resolve. The dialog stays open with the form
      // filled in so the user can retry or correct it.
      toast.error(getApiErrorMessage(err, "Failed to create asset"));
    } finally {
      setSubmitting(false);
    }
  };

  const open = (a: Asset) => openRecord(`/rentals/assets/${a.id}`);

  // Rows hold a View button, so they follow Rate Sheets: no button role, and
  // Enter/Space open the row only when the row itself has focus.
  const rowKeys = (a: Asset) => (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      open(a);
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Render                                                           */
  /* ---------------------------------------------------------------- */

  return (
    <div data-testid="asset-registry-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Surface rentals"
        title="Rental Equipment"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <>
            {/* No colour class: it inherits the band's near-white. */}
            <button
              type="button"
              onClick={() => navigate('/import/rental-assets')}
              className="fi-action fi-action--standard border border-white/25 hover:border-white/60"
            >
              <Upload className="size-4" aria-hidden="true" />
              Import
            </button>
            <button
              type="button"
              onClick={() => {
                setAddForm(blankForm());
                setAddOpen(true);
              }}
              className="fi-action fi-action--standard fi-action--primary"
            >
              <Plus className="size-4" aria-hidden="true" />
              Add Asset
            </button>
          </>
        }
        /* The old strip had a "Daily Revenue" tile that summed the day rates of
           assets on site. A sum of list rates is not revenue, and its "on site"
           status was one the API never writes, so it read $0 for every tenant. */
        stats={[
          { icon: <Boxes className="size-4" aria-hidden="true" />, label: "Assets", sub: "Total units", tone: "neutral", loading, value: tally("all") },
          { icon: <PackageCheck className="size-4" aria-hidden="true" />, label: "Available", sub: "In the yard", tone: "success", loading, value: tally("available") },
          { icon: <Truck className="size-4" aria-hidden="true" />, label: "On Rent", sub: "Out on order", tone: "info", loading, value: tally("rented") },
          { icon: <Wrench className="size-4" aria-hidden="true" />, label: "Maintenance", sub: "In shop", tone: "accent", loading, value: tally("maintenance") },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="scrollbar-hide flex flex-1 gap-2 overflow-x-auto">
              {CHIPS.filter((c) => c.key !== "other" || (counts?.other ?? 0) > 0).map((c) => {
                const active = chip === c.key;
                return (
                  <button
                    key={c.key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setFilter("status", c.key === "all" ? "" : c.key)}
                    className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                      active
                        ? "bg-fi-amber text-fi-amber-ink"
                        : "border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text"
                    }`}
                  >
                    {c.label}
                    {!loading && !failed && counts && (
                      <span className={`tabular-nums ${active ? "text-fi-amber-ink" : "text-fieldiq-muted"}`}>{counts[c.key]}</span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              {/* Native and labelled: ui/select prints the raw value in its trigger. */}
              <select
                aria-label="Filter by category"
                value={categoryFilter}
                onChange={(e) => setFilter("category", e.target.value === "All" ? "" : e.target.value)}
                className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-1.5 text-sm text-fieldiq-text focus:border-fi-amber focus:outline-none"
              >
                <option value="All">All categories</option>
                {categoryNames.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <div className="relative sm:w-72">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
                <input
                  type="text"
                  placeholder="Search by name or asset number..."
                  aria-label="Search assets"
                  value={searchInput}
                  onChange={(e) => onSearchInput(e.target.value)}
                  className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card py-1.5 pl-10 pr-3 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
                />
              </div>
            </div>
          </div>

          <section
            aria-labelledby="asset-register-title"
            className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            <header className="border-b border-fieldiq-border px-4 py-4">
              <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Equipment register</p>
              <h2 id="asset-register-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
                Rental equipment
              </h2>
            </header>

            {failed ? (
              <LoadFailureNotice error={queryFailureError(assetsQuery)} subject="rental assets" />
            ) : loading ? (
              <div className="space-y-3 p-4" aria-hidden="true">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-12 animate-pulse rounded-fi-control bg-fieldiq-dark/60" />
                ))}
              </div>
            ) : assets && assets.length === 0 ? (
              <p className="px-4 py-16 text-center text-sm text-fieldiq-muted">No rental equipment yet</p>
            ) : displayFiltered.length === 0 ? (
              <p className="px-4 py-16 text-center text-sm text-fieldiq-muted">No assets match these filters</p>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[860px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                      <tr className="border-l-4 border-l-transparent text-xs font-bold uppercase tracking-wider text-fieldiq-muted">
                        <th className="px-4 py-3">Asset</th>
                        <th className="px-4 py-3">Category</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Location</th>
                        <th className="px-4 py-3 text-right">Day rate</th>
                        <th className="w-[90px] px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {displayFiltered.map((a) => (
                        <tr
                          key={a.id}
                          data-testid="asset-row"
                          tabIndex={0}
                          onClick={() => open(a)}
                          onKeyDown={rowKeys(a)}
                          className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition-colors hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${skinOf(a.status).rail}`}
                        >
                          <td className="px-4 py-3">
                            <p className="text-sm font-extrabold text-fieldiq-text">{a.name}</p>
                            <p className="font-mono text-xs text-fieldiq-muted">{a.tag ?? "—"}</p>
                          </td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{a.category ?? "—"}</td>
                          <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                          <td className="px-4 py-3 text-sm text-fieldiq-muted">{a.yard ?? "—"}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-bold tabular-nums text-fieldiq-text">
                            {a.rate == null ? "—" : `${fmtCurrency(a.rate)} / day`}
                          </td>
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              onClick={() => open(a)}
                              aria-label={`View ${a.name}`}
                              className="rounded-fi-pill border border-fieldiq-border bg-fieldiq-dark px-3 py-1 text-xs font-bold text-fieldiq-text hover:border-fi-amber"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="space-y-3 p-4 md:hidden">
                  {displayFiltered.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      data-testid="asset-card"
                      onClick={() => open(a)}
                      className="block w-full rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4 text-left"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate font-extrabold text-fieldiq-text">{a.name}</p>
                          <p className="font-mono text-xs text-fieldiq-muted">{a.tag ?? "—"}</p>
                        </div>
                        <StatusBadge status={a.status} />
                      </div>
                      <p className="mt-2 text-sm text-fieldiq-text">
                        {a.category ?? "—"} · {a.yard ?? "—"}
                      </p>
                      <p className="text-sm font-bold tabular-nums text-fieldiq-text">
                        {a.rate == null ? "—" : `${fmtCurrency(a.rate)} / day`}
                      </p>
                    </button>
                  ))}
                </div>

                <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
                  Showing {displayFiltered.length} of {assets?.length ?? 0} assets
                </p>
              </>
            )}
          </section>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  ADD ASSET DIALOG                                             */}
      {/* ============================================================ */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Asset</DialogTitle>
          </DialogHeader>
          <AssetFormFields
            form={addForm}
            setForm={(fn) => setAddForm(fn)}
            idPrefix="add-asset"
            categoryNames={categoryNames}
            onManageCategories={() => setManageCategoriesOpen(true)}
          />
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setAddOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleAddSubmit} disabled={submitting}>
              {submitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Add Asset
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* MANAGE CATEGORIES DIALOG */}
      <ManageCategoriesDialog open={manageCategoriesOpen} onOpenChange={setManageCategoriesOpen} />
    </div>
  );
};

export default AssetRegistry;
